import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { UserDenormalizedWithoutLook } from "./user"
import { LiveCommentId, LiveCommentIdSchema } from "../id"
import { CreatedAt, UpdatedAt } from "./system"
import { OGP } from "./ogp"
import { validateUserFreeInputText } from "../user-free-input-text-constraint"
import { LivePowerDenormalized } from "./live-power"
import { MediaImage } from "../common"

export const LiveCommentKind = z.union([
  z.literal("text"),
  z.literal("emotion"),
  z.literal("image"),
  z.literal("joinToSpace"),
  z.literal("silentJoinToSpace"),
  z.literal("power"),
  z.literal("reaction"),
  z.literal("aggregated-reactions"),
  z.literal("followOwner"),
  z.literal("superCallOn"),
])

/**
 * 許容されるリアクションの種別
 * クライアント側の GetEmoteListAction で定義されている emote ID と一致させる
 */
export const LiveReactionEmoteId = z.enum([
  "po",
  "thumbsup",
  "crying",
  "surprised",
  "question",
  "laughter",
  "angry",
  "thinking",
  "clapping",
  "happy",
  "depressed",
])
export type LiveReactionEmoteId = z.infer<typeof LiveReactionEmoteId>

/**
 * 集約されたリアクションのカウント
 * キーはリアクションの種別（LiveReactionEmoteId）、値はカウント数
 * 不正なキーは Zod バリデーションでエラーになる
 */
export const AggregatedReactionCounts = z.partialRecord(LiveReactionEmoteId, z.int().nonnegative())
export type AggregatedReactionCounts = z.infer<typeof AggregatedReactionCounts>

/**
 * コメント投稿時のユーザーの状態
 * - viewer: 視聴者として投稿（メンバーではない視聴者、またはメンバーだが通話中でない場合）
 * - talking: 会話中（通話中）のメンバーとして投稿
 */
export const LiveCommentUserKind = z.union([
  z.literal("viewer"),
  z.literal("talking"),
])
export type LiveCommentUserKind = z.infer<typeof LiveCommentUserKind>

export const LiveCommentDocumentMeta = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("text"),
    value: z.string(),
    ogp: OGP.optional(),
  }),
  z.object({
    kind: z.literal("emotion"),
    value: z.string(),
  }),
  z.object({
    kind: z.literal("image"),
    value: z.string().optional(),
    media: MediaImage.optional().describe("CDN variants 付き画像。value と同じ画像を指すが、variants で複数解像度の URL を保持する"),
  }),
  z.object({
    kind: z.literal("joinToSpace"),
  }),
  z.object({
    kind: z.literal("silentJoinToSpace"),
  }),
  z.object({
    kind: z.literal("power"),
    value: LivePowerDenormalized,
  }),
  z.object({
    kind: z.literal("reaction"),
    value: z.object({
      counts: AggregatedReactionCounts,
    }),
  }),
  z.object({
    kind: z.literal("aggregated-reactions"),
    value: AggregatedReactionCounts,
  }),
  z.object({
    kind: z.literal("followOwner"),
  }),
  z.object({
    kind: z.literal("superCallOn"),
  }),
])

export type LiveCommentDocumentMeta = z.infer<typeof LiveCommentDocumentMeta>

export const LiveCommentDocumentBody = z.intersection(
  LiveCommentDocumentMeta,
  z.object({
    user: UserDenormalizedWithoutLook.optional(),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
    priority: z.int(),
    user_kind: LiveCommentUserKind.optional(),
  }),
)

export type LiveCommentDocumentBody = z.infer<typeof LiveCommentDocumentBody>

export const LiveCommentDocument = z.intersection(
  LiveCommentDocumentBody,
  z.object({
    id: LiveCommentIdSchema,
  })
)

export type LiveCommentDocument = z.infer<typeof LiveCommentDocument>

export class LiveCommentDocuments {

  static readonly MessageMinLength = 1
  static readonly MessageMaxLength = 1024

  static toDocumentData(doc: LiveCommentDocument): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): LiveCommentDocument | null {
    if (!doc) {
      return null
    }

    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: LiveCommentId(doc.id),
      created_at: data["created_at"],
      updated_at: data["updated_at"],
      kind: data["kind"],
      value: data["value"],
      user: data["user"],
      ogp: data.ogp,
      media: data["media"],
      priority: data["priority"],
      user_kind: data["user_kind"],
    }
  }
}

/**
 * コメントの優先度
 * 1-100: ユーザーコメント（クライアント側で計算）
 * 101以上: システムコメント（サーバー側で設定）
 */
export const LiveCommentPriority = z.int().min(1).max(100)

/**
 * システムコメント（joinToSpace, silentJoinToSpace, power, reaction, aggregated-reactions, followOwner, superCallOn）の優先度
 * ユーザーコメント（1-100）よりも高い値を設定し、フィルタリングされないようにする
 */
export const SYSTEM_COMMENT_PRIORITY = 101

/**
 * ユーザーコメントのデフォルト優先度
 * クライアントが priority を指定しなかった場合に使用される（過去バージョンとの互換性のため）
 */
export const DEFAULT_USER_COMMENT_PRIORITY = 50

export const CreateLiveCommentRequest = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("text"),
    value: validateUserFreeInputText(
      {minLength: LiveCommentDocuments.MessageMinLength, maxLength: LiveCommentDocuments.MessageMaxLength},
    ),
    priority: LiveCommentPriority.optional(),
  }),
  z.object({
    kind: z.literal("emotion"),
    value: validateUserFreeInputText(
      {minLength: LiveCommentDocuments.MessageMinLength, maxLength: LiveCommentDocuments.MessageMaxLength},
    ),
    priority: LiveCommentPriority.optional(),
  }),
  z.object({
    kind: z.literal("image"),
    priority: LiveCommentPriority.optional(),
  }),
])

export type CreateLiveCommentRequest = z.infer<typeof CreateLiveCommentRequest>
